import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  inject,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { PropertyUnit } from 'app/modules/projects/details/tenants/tenants.types';
import { PropertyTenantsService } from '../../../tenants/tenants.service';
import { Account } from 'app/core/models/account.types';
import { PanelDef } from 'app/shared/components/panels/panels.types';
import { Property } from 'app/modules/projects/projects.types';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { FeatureFlag } from '@core/enums';
import { AccountState } from '@state/account/state';
import { Store } from '@ngxs/store';

export interface UnitLease {
  id: number;
  name: string;
  usps_fin_sub: string;
  lease_end_date: string;
  lease_start_date: string;
  annual_rent: string;
  maintenance: string;
  interior_sq_ft: string;
}

@Component({
  selector: 'property-panel-unit-lease',
  templateUrl: './unit-lease.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsUnitLeaseComponent {
  @Input() property: Property;
  @Input() panelDef: PanelDef<any>;
  @Input() account: Account;
  @Input() readonly = false;
  @Input() data: any;
  @Input() canEditPanelDef: boolean = true;
  @Output() onEditPanelDef: EventEmitter<any> = new EventEmitter();

  private readonly destroyRef = inject(DestroyRef);

  titleFieldDef: any;
  panelName: string;
  editMode: boolean;
  changeTitleMode: boolean = false;
  propertyUnits: PropertyUnit[];
  unitLeases: UnitLease[] = [];
  displayColumns: string[];
  unitLeaseDataSource: MatTableDataSource<any>;

  columnDefs: ColumnDef[] = [];
  newTable: boolean;

  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _propertyTenantsService: PropertyTenantsService,
    private readonly store: Store,
  ) {
    this.unitLeaseDataSource = new MatTableDataSource();
    this.displayColumns = [
      'name',
      'usps_fin_sub',
      'lease_start_date',
      'lease_end_date',
      'interior_sq_ft',
      'annual_rent',
      'maintenance',
    ];
  }

  ngOnInit() {
    this.checkTable();
    this.titleFieldDef = {
      name: 'panel_name',
      label: this.panelDef.title,
      type: 'text',
    };

    this._propertyTenantsService.propertyUnits$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((units: PropertyUnit[]) => {
        if (units) {
          this.propertyUnits = units;

          this.unitLeases = [];
          this.propertyUnits.forEach((unit) => {
            let propertyTenants = unit.property_tenants?.sort((a, b) => a?.lease_start_date - b?.lease_start_date);
            for (let propertyTenant of propertyTenants) {
              let usps_fin_sub = unit.custom_field_values?.find((field) => field.name === 'usps_fin_sub')?.val;

              if (usps_fin_sub) {
                this.unitLeases.push({
                  name: unit.unit_number,
                  usps_fin_sub: unit.custom_field_values?.find((field) => field.name === 'usps_fin_sub')?.val,
                  lease_end_date: propertyTenant?.lease_end_date ? propertyTenant.lease_end_date : '',
                  lease_start_date: propertyTenant?.lease_start_date ? propertyTenant.lease_start_date : '',
                  annual_rent: propertyTenant?.annual_rent ? propertyTenant.annual_rent : '',
                  maintenance: propertyTenant?.maintenance ? propertyTenant.maintenance : '',
                  interior_sq_ft: propertyTenant?.interior_sq_ft ? propertyTenant.interior_sq_ft : '',
                  id: unit.id,
                });
              }
            }
          });

          this.unitLeaseDataSource = new MatTableDataSource();
          this.unitLeaseDataSource.data = this.unitLeases;

          this._changeDetectorRef.markForCheck();
        }
      });

    if (this.newTable) {
      this.setColDefs();
    }
  }

  toggleChangeTitleMode() {
    this.changeTitleMode = true;
  }

  toggleEditMode(editMode: boolean | null = null): void {
    if (editMode === null) {
      this.editMode = !this.editMode;
    } else {
      this.editMode = editMode;
    }
    this.changeTitleMode = false;
    this._changeDetectorRef.markForCheck();
  }

  cancelEdits(): void {
    this.toggleEditMode(false);
  }

  update(params) {
    let currentKey = Object.keys(params)[0];
    if (currentKey === 'panel_name') {
      this.panelName = params.panel_name;
    }
  }

  saveLayout(): void {
    if (this.panelName) {
      this.panelDef.title = this.panelName;
    }
    this.onEditPanelDef.emit(this.panelDef);
    this.toggleEditMode(false);
  }

  setColDefs(): void {
    this.columnDefs = [
      {
        label: 'Name',
        name: 'name',
        type: 'text',
        tsTableOptions: {
          headerName: 'Name',
          field: 'name',
          editable: false,
          flex: 1,
        },
      },
      {
        label: 'Fin Sub',
        name: 'fin_sub',
        type: 'text',
        tsTableOptions: {
          headerName: 'Fin Sub',
          field: 'usps_fin_sub',
          editable: false,
          flex: 1,
        },
      },
      {
        label: 'Lease Start Date',
        name: 'lease_start_date',
        type: 'date',
        tsTableOptions: {
          headerName: 'Lease Start Date',
          field: 'lease_start_date',
          editable: false,
          flex: 1,
        },
      },
      {
        label: 'Lease End Date',
        name: 'lease_end_date',
        type: 'date',
        tsTableOptions: {
          headerName: 'Lease End Date',
          field: 'lease_end_date',
          editable: false,
          flex: 1,
        },
      },
      {
        label: 'Interior Sq Ft',
        name: 'interior_sq_ft',
        type: 'text',
        tsTableOptions: {
          headerName: 'Interior Sq Ft',
          field: 'interior_sq_ft',
          editable: false,
          flex: 1,
        },
      },
      {
        label: 'Annual Rent',
        name: 'annual_rent',
        type: 'money',
        tsTableOptions: {
          headerName: 'Annual Rent',
          field: 'annual_rent',
          editable: false,
          flex: 1,
        },
      },
      {
        label: 'Maintenance',
        name: 'maintenance',
        type: 'money',
        tsTableOptions: {
          headerName: 'Maintenance',
          field: 'maintenance',
          editable: false,
          flex: 1,
        },
      },
    ];
  }

  checkTable() {
    this.newTable = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_TABLE));
  }
}
